import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface MfeManifest {
  [key: string]: string;
}

export interface MfeState {
  isLoaded: boolean;
  isLoading: boolean;
  error: string | null;
  scriptUrl: string | null;
}

/**
 * MfeLoaderService - Singleton service for loading MFE scripts
 *
 * This service handles:
 * 1. Fetching the MFE manifest (once at app startup)
 * 2. Loading the MFE script
 * 3. Caching the loaded state to prevent duplicate loads
 *
 * Usage:
 * - Call `initialize()` via APP_INITIALIZER at app startup
 * - Inject into components and check `isReady$` or `state$`
 *
 * @example
 * ```typescript
 * // In component
 * constructor(private mfeLoader: MfeLoaderService) {}
 *
 * ngOnInit() {
 *   // Script is already loaded, just render <pipa-mfe>
 *   this.isReady$ = this.mfeLoader.isReady$;
 *   this.error$ = this.mfeLoader.error$;
 * }
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class MfeLoaderService {
  private readonly MFE_SCRIPT_NAME = 'pipa-mfe.js';

  private stateSubject = new BehaviorSubject<MfeState>({
    isLoaded: false,
    isLoading: false,
    error: null,
    scriptUrl: null
  });

  /** Observable of the full MFE state */
  readonly state$: Observable<MfeState> = this.stateSubject.asObservable();

  /** Observable that emits true when MFE is ready to use */
  get isReady$(): Observable<boolean> {
    return new Observable(subscriber => {
      this.stateSubject.subscribe(state => {
        subscriber.next(state.isLoaded && !state.error);
      });
    });
  }

  /** Observable of any error that occurred */
  get error$(): Observable<string | null> {
    return new Observable(subscriber => {
      this.stateSubject.subscribe(state => {
        subscriber.next(state.error);
      });
    });
  }

  /** Current state snapshot */
  get state(): MfeState {
    return this.stateSubject.getValue();
  }

  /** Whether the MFE script is loaded and ready */
  get isReady(): boolean {
    const state = this.state;
    return state.isLoaded && !state.error;
  }

  /**
   * Initialize the MFE loader - fetches manifest and loads script.
   * Should be called once at app startup via APP_INITIALIZER.
   *
   * @returns Promise that resolves when script is loaded (or rejects on error)
   */
  async initialize(): Promise<void> {
    // Skip if already loaded or loading
    if (this.state.isLoaded || this.state.isLoading) {
      return;
    }

    this.updateState({ isLoading: true, error: null });

    try {
      // 1. Determine manifest URL based on environment
      const manifestUrl = this.getManifestUrl();

      // 2. Fetch the manifest
      const manifest = await this.fetchManifest(manifestUrl);

      // 3. Get the versioned script URL
      const scriptUri = manifest[this.MFE_SCRIPT_NAME];
      if (!scriptUri) {
        throw new Error(`${this.MFE_SCRIPT_NAME} not found in manifest`);
      }

      // 4. Build full script URL
      const scriptUrl = this.buildScriptUrl(scriptUri);

      // 5. Load the script
      await this.loadScript(scriptUrl);

      this.updateState({
        isLoaded: true,
        isLoading: false,
        scriptUrl
      });

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error loading MFE';
      console.error('[MfeLoader] Failed to load MFE:', errorMessage);

      this.updateState({
        isLoading: false,
        error: errorMessage
      });

      // Don't throw - allow app to continue even if MFE fails
      // Components can handle the error state gracefully
    }
  }

  /**
   * Retry loading the MFE script (useful after a failure)
   */
  async retry(): Promise<void> {
    this.updateState({
      isLoaded: false,
      isLoading: false,
      error: null,
      scriptUrl: null
    });
    return this.initialize();
  }

  /**
   * Get the manifest URL based on current environment
   */
  private getManifestUrl(): string {
    const isLocal = window.location.hostname.includes('local');

    if (isLocal) {
      return `${environment.mfeBaseUrl}/wc/manifest.json`;
    }

    return `https://${window.location.hostname}/mfe/manifest.json`;
  }

  /**
   * Build the full script URL from the manifest URI
   */
  private buildScriptUrl(scriptUri: string): string {
    const isLocal = window.location.hostname.includes('local');

    if (isLocal) {
      return `${environment.mfeBaseUrl}/wc/${scriptUri}`;
    }

    return `https://${window.location.hostname}/mfe/${scriptUri}`;
  }

  /**
   * Fetch and parse the MFE manifest
   */
  private async fetchManifest(url: string): Promise<MfeManifest> {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch manifest: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Load the MFE script into the document
   */
  private loadScript(src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if script is already in the document
      const existingScript = document.querySelector(`script[src="${src}"]`);
      if (existingScript) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.type = 'module';
      script.src = src;
      script.crossOrigin = 'anonymous';

      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Failed to load script: ${src}`));

      document.head.appendChild(script);
    });
  }

  /**
   * Update the state with partial values
   */
  private updateState(partial: Partial<MfeState>): void {
    this.stateSubject.next({
      ...this.state,
      ...partial
    });
  }
}

/**
 * Factory function for APP_INITIALIZER
 * This ensures the MFE script is loaded before the app fully initializes
 */
export function initializeMfeLoader(mfeLoader: MfeLoaderService): () => Promise<void> {
  return () => mfeLoader.initialize();
}
